import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface HeroBadge {
  icon?: string;
  title: string;
  subtitle: string;
}

export interface HeroContent {
  title: string;
  subtitle: string;
  ctaText: string;
  imageUrl: string;
  badges: HeroBadge[];
  /** True when the org has never saved a hero, so this is the built-in default copy. */
  isDefault?: boolean;
}

export const DEFAULT_HERO_BADGES: HeroBadge[] = [
  { icon: 'Shield', title: 'Fully insured', subtitle: 'Peace of mind' },
  { icon: 'ThumbsUp', title: '100% satisfaction', subtitle: 'Or we fix it free' },
  { icon: 'Clock', title: 'Flexible times', subtitle: '7 days a week' },
];

const DEFAULT_HERO_CONTENT: HeroContent = {
  title: 'One-off or regular cleaning',
  subtitle:
    "We provide reliable, quality cleaning for busy households and businesses, with clear, upfront pricing so you always know exactly what you're paying for. It's a straightforward service designed to keep your space looking its best without any hidden surprises.",
  ctaText: 'Book my cleaning',
  imageUrl: '',
  badges: DEFAULT_HERO_BADGES,
  isDefault: true,
};

/**
 * Homepage hero section content from page_content, scoped to the active org.
 * Falls back to DEFAULT_HERO_CONTENT when no customisation has been saved.
 */
export function useHeroContent(orgId: string | undefined) {
  return useQuery({
    queryKey: ['homepage-hero', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<HeroContent> => {
      const { data, error } = await supabase
        .from('page_content')
        .select('content')
        .eq('organization_id', orgId!)
        .eq('page', 'homepage')
        .eq('section', 'hero')
        .maybeSingle();

      if (error) throw error;

      if (data?.content) {
        const heroData = data.content as unknown as HeroContent;
        return {
          title: heroData.title || DEFAULT_HERO_CONTENT.title,
          subtitle: heroData.subtitle || DEFAULT_HERO_CONTENT.subtitle,
          ctaText: heroData.ctaText || DEFAULT_HERO_CONTENT.ctaText,
          imageUrl: heroData.imageUrl || '',
          badges: Array.isArray(heroData.badges) && heroData.badges.length > 0
            ? heroData.badges.map((badge) => ({
                icon: badge.icon || 'Star',
                title: badge.title || '',
                subtitle: badge.subtitle || '',
              })).filter((badge) => badge.title || badge.subtitle)
            : DEFAULT_HERO_BADGES,
          isDefault: false,
        };
      }

      return DEFAULT_HERO_CONTENT;
    },
  });
}
